import type { ReactNode } from 'react';
import { BrandMark } from '@/ui/Icon';
import { Callout } from '@/ui/bits';
import { AuthBrand, AuthFrame } from './AuthFrame';

// What shows while Clientbook opens, and when the server cannot be reached or is still getting ready.

export type BootState =
  | { kind: 'opening' }
  /** The server is up but not ready yet; `problem` is its explanation when it has one. */
  | { kind: 'starting'; problem: string | null }
  | { kind: 'offline' }
  | { kind: 'error'; message: string };

export function Boot({ state, onRetry }: { state: BootState; onRetry: () => void }) {
  if (state.kind === 'opening') {
    return (
      <AuthFrame label="Opening Clientbook">
        <div className="boot" role="status">
          <BrandMark />
          <p>Opening Clientbook</p>
        </div>
      </AuthFrame>
    );
  }

  const retry = (
    <button type="button" className="btn btn-tile btn-sm" onClick={onRetry}>
      Try again now
    </button>
  );

  return (
    <AuthFrame label="Clientbook cannot open yet">
      <div className="auth-card">
        <AuthBrand />
        {state.kind === 'starting' ? <Starting problem={state.problem} retry={retry} /> : null}
        {state.kind === 'offline' ? (
          <>
            <h1 className="auth-h">You are offline</h1>
            <p className="auth-sub">Clientbook cannot reach its server. It tries again every few seconds and opens as soon as the connection is back.</p>
            <div className="auth-body">
              <Callout tone="info" icon="cloud" title="Waiting for the connection" actions={retry} role="status">
                Check your network connection, or try again now.
              </Callout>
            </div>
          </>
        ) : null}
        {state.kind === 'error' ? (
          <>
            <h1 className="auth-h">Clientbook could not open</h1>
            <p className="auth-sub">The server answered with a problem while loading your account.</p>
            <div className="auth-body">
              <Callout tone="danger" title={state.message} actions={retry} role="alert" />
            </div>
          </>
        ) : null}
      </div>
    </AuthFrame>
  );
}

function Starting({ problem, retry }: { problem: string | null; retry: ReactNode }) {
  const missingDatabase = !!problem && problem.includes('DATABASE_URL');
  return (
    <>
      <h1 className="auth-h">{problem ? 'Clientbook is not ready yet' : 'Clientbook is starting'}</h1>
      <p className="auth-sub">This page checks again every few seconds and opens Clientbook as soon as the server is ready.</p>
      <div className="auth-body">
        {problem ? (
          <Callout tone="warn" title={problem} actions={retry} role="status" className="boot-problem">
            {missingDatabase ? (
              <ol>
                <li>
                  In your host&apos;s settings for this service, add <code>DATABASE_URL</code> with the connection string of your PostgreSQL
                  database.
                </li>
                <li>
                  Run <code>npm run db:setup</code> once to create the tables and the first accounts.
                </li>
                <li>Restart the service.</li>
              </ol>
            ) : (
              <span>The server retries on its own. If this stays for more than a few minutes, check the service logs on your host.</span>
            )}
          </Callout>
        ) : (
          <div className="boot" role="status">
            <BrandMark />
            <p>Getting the database ready</p>
          </div>
        )}
      </div>
    </>
  );
}
